/*
  Your account, and — if you administer this deployment — everybody else's.

  ONE MODAL FOR BOTH, gated inside rather than split in two, which is the same choice `MembersEditor`
  makes and for the same reason: the two audiences overlap almost entirely (an administrator also has
  a password to change), and a second entry point that most users never see is a second thing to find.
  A non-administrator simply does not get the roster — the server enforces that regardless; hiding it
  here is a courtesy.

  NOT A COMMAND, so none of this is in the change log. An account is not part of a project's design:
  it does not export, it is not per scenario, and reverting it as a step in the undo chain would make
  no sense. It lives on its own routes, and this panel refetches rather than listening on the socket.

  THE SERVER'S SENTENCE IS SHOWN VERBATIM. Every refusal here is a rule this component does not
  enforce — the last administrator, your own account, the current password — and a client-side
  paraphrase of a rule the client does not apply is how the two drift apart.
*/
import { useEffect, useState } from 'react';
import type { AccessEvent, AccessEventKind, AccessRow, AdminUser, User } from '@integrate/contracts';
import { api, ApiError } from '../api.js';
import { Modal } from '../ui/Modal.js';
import { Loading } from '../ui/States.js';

/**
 * One access event as a sentence.
 *
 * A `Record` over the kind rather than a chain of `if`s, so a new kind in contracts is a BUILD ERROR
 * here naming the kind — the same shape `FIELD_POLICY` uses in the redactor and `COMMAND_LABELS` uses
 * in `describe.ts`. A log row that renders as its own enum value is a row nobody reads.
 *
 * The subject's name is the SUBJECT of the sentence and the actor is a separate column, because the
 * two questions — "what happened to this person" and "who did it" — are read by scanning different
 * places, and a sentence carrying both makes each one harder to find.
 */
const ACCESS_EVENT_LABEL: Record<AccessEventKind, (e: AccessEvent) => string> = {
  'user.created': (e) => `${e.subjectName ?? 'An account'} was created`,
  'user.deactivated': (e) => `${e.subjectName ?? 'An account'} was deactivated`,
  'user.reactivated': (e) => `${e.subjectName ?? 'An account'} was reactivated`,
  'user.admin-granted': (e) => `${e.subjectName ?? 'An account'} was made an administrator`,
  'user.admin-revoked': (e) => `${e.subjectName ?? 'An account'} is no longer an administrator`,
  'user.renamed': (e) =>
    `${String(e.detail.from ?? 'An account')} was renamed to ${String(e.detail.to ?? e.subjectName ?? '')}`,
  'user.password-reset': (e) => `${e.subjectName ?? 'An account'}’s password was reset`,
  'member.granted': (e) =>
    `${e.subjectName ?? 'Somebody'} was given ${String(e.detail.role ?? 'access')} on ${projectLabel(e)}`,
  'member.role-changed': (e) =>
    `${e.subjectName ?? 'Somebody'} went from ${String(e.detail.from ?? '?')} to ${String(e.detail.to ?? '?')} on ${projectLabel(e)}`,
  'member.removed': (e) => `${e.subjectName ?? 'Somebody'} was removed from ${projectLabel(e)}`,
  'member.left': (e) => `${e.subjectName ?? 'Somebody'} left ${projectLabel(e)}`,
  // Actor and subject are the same person on an export, so the sentence names the artefact instead of
  // repeating them; "a slice" tells the reader the file was a scoped member's narrowed view, not the roster.
  'export.workbook': (e) =>
    `${e.subjectName ?? 'Somebody'} exported the workbook${e.detail.scoped ? ' (a slice)' : ''} of ${projectLabel(e)}`,
  'export.deck': (e) =>
    `${e.subjectName ?? 'Somebody'} exported the deck${e.detail.scoped ? ' (a slice)' : ''} of ${projectLabel(e)}`,
  'export.buyer': (e) =>
    `${e.subjectName ?? 'Somebody'} exported the buyer pack${e.detail.scoped ? ' (a slice)' : ''} of ${projectLabel(e)}`,
  // Never "(a slice)": the route refuses restricted grants, so a pack is always the whole project —
  // and the change number is the sentence's point, because it names the moment the files photograph.
  'export.steerco': (e) =>
    `${e.subjectName ?? 'Somebody'} issued SteerCo pack ${e.detail.name ? `"${String(e.detail.name)}" ` : ''}of ${projectLabel(e)} at change #${String(e.detail.seq ?? '?')}`,
};

/** A deleted project keeps its rows — there is no foreign key — so the name may be gone. */
function projectLabel(e: AccessEvent): string {
  return e.projectName ?? 'a project that has since been deleted';
}

function describeAccessEvent(e: AccessEvent): string {
  return ACCESS_EVENT_LABEL[e.kind](e);
}

export function AccountModal({
  currentUserId,
  currentUserName,
  isAdmin,
  onRenamed,
  onClose,
}: {
  currentUserId: string;
  currentUserName: string;
  isAdmin: boolean;
  /**
   * The account after a rename, so the header and the presence strip stop showing the old name.
   *
   * Handed up rather than fetched: the route has just returned the updated `User`, so asking
   * `/api/me` for it again would be a round trip to learn what is already in hand.
   */
  onRenamed(user: User): void;
  onClose(): void;
}): JSX.Element {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [access, setAccess] = useState<AccessRow[] | null>(null);
  const [log, setLog] = useState<AccessEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Your own name
  const [myName, setMyName] = useState(currentUserName);

  // Change-your-own-password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Create-a-user
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [makeAdmin, setMakeAdmin] = useState(false);

  const reload = async (): Promise<void> => {
    if (!isAdmin) return;
    try {
      /*
        Both lists together. The access list changes when a MEMBERSHIP changes, which happens
        elsewhere — but it also changes when an account is deactivated or renamed, which happens
        here, and two refetch paths that can disagree about the same screen is the drift worth
        avoiding for one extra request on a surface that is opened rarely.
      */
      const [{ users: rows }, { access: grants }, { events }] = await Promise.all([
        api.adminUsers(),
        api.adminAccess(),
        api.adminAccessLog(),
      ]);
      setUsers(rows);
      setAccess(grants);
      setLog(events);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the users.');
    }
  };

  /*
    One fetch when the modal opens, and none after: every write refetches through `run`.

    Nothing streams here, deliberately. Accounts are not part of the project's event stream — they
    are not commands, they carry no sequence number, and a colleague being created in another tab is
    not something this screen has to learn about mid-edit.
  */
  useEffect(() => {
    void reload();
  }, [isAdmin]);

  /**
   * The one write funnel: clear, act, refetch, and surface whatever the server said.
   *
   * `done` is separate from `error` because several of these actions produce no visible change in the
   * list — resetting a password, changing your own — and a screen that says nothing after a
   * destructive-sounding click reads as a failure.
   */
  const run = async (action: () => Promise<void>, success: string): Promise<void> => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await action();
      await reload();
      setDone(success);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const renameSelf = (): void => {
    const next = myName.trim();
    if (!next || next === currentUserName) return;
    void run(async () => {
      const { user } = await api.updateOwnProfile(next);
      onRenamed(user);
    }, 'Your name has been changed. Colleagues will see it on their next reconnect.');
  };

  const changeOwnPassword = (): void => {
    if (!currentPassword || !newPassword) return;
    void run(async () => {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
    }, 'Your password has been changed. Your other sessions have been signed out.');
  };

  const create = (): void => {
    if (!email.trim() || !name.trim() || !password) return;
    void run(async () => {
      await api.createUser({
        email: email.trim(),
        name: name.trim(),
        password,
        isAdmin: makeAdmin,
      });
      setEmail('');
      setName('');
      setPassword('');
      setMakeAdmin(false);
    }, 'Account created. Give them the password directly — it is not sent anywhere.');
  };

  return (
    <Modal
      testId="account-modal"
      title={isAdmin ? 'Account & users' : 'Your account'}
      onClose={onClose}
    >
          {error && (
            <p className="bad" data-testid="account-error" role="alert">
              {error}
            </p>
          )}
          {done && (
            <p className="muted" data-testid="account-done" role="status">
              {done}
            </p>
          )}

          {/*
            YOUR NAME, and it is not a vanity field. It is the string every other person in the
            product sees — the presence strip, every change-log row, every proposal and every
            "applied by". It could be set once at account creation and never again, by anybody,
            including an administrator whose only recourse for a typo was `psql`.
          */}
          <h5 className="asm-grid-h">Your name</h5>
          <p className="muted asm-help">
            This is what colleagues see against your changes. Your email is your identity here and
            cannot be changed.
          </p>
          <div className="field">
            <label htmlFor="my-name">Name</label>
            <input
              id="my-name"
              data-testid="my-name"
              value={myName}
              disabled={busy}
              onChange={(e) => setMyName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') renameSelf();
              }}
            />
          </div>
          <button
            data-testid="change-my-name"
            disabled={busy || myName.trim() === '' || myName.trim() === currentUserName}
            onClick={renameSelf}
          >
            Change my name
          </button>

          <h5 className="asm-grid-h">Your password</h5>
          <p className="muted asm-help">
            Changing it signs out your other sessions. This one stays signed in.
          </p>
          <div className="field">
            <label htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              data-testid="current-password"
              type="password"
              value={currentPassword}
              disabled={busy}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              data-testid="new-password"
              type="password"
              value={newPassword}
              disabled={busy}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') changeOwnPassword();
              }}
            />
          </div>
          <button
            className="primary"
            data-testid="change-password"
            disabled={busy || !currentPassword || !newPassword}
            onClick={changeOwnPassword}
          >
            Change my password
          </button>

          {isAdmin && (
            <>
              <h5 className="asm-grid-h">Everybody on this deployment</h5>
              <p className="muted asm-help">
                Deactivating somebody ends their sessions immediately and disconnects any tab they
                have open. Their work stays exactly where it is.
              </p>
              {users === null ? (
                <Loading what="the user list" testId="users-loading" />
              ) : (
                <table className="tsa-table" data-testid="users-table">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Role</th>
                      <th scope="col">Status</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const isYou = u.id === currentUserId;
                      return (
                        <tr key={u.id} data-testid={`user-row-${u.id}`}>
                          <td>
                            {u.name}
                            {isYou && <span className="muted"> · you</span>}
                          </td>
                          <td>{u.email}</td>
                          <td>{u.isAdmin ? 'Administrator' : 'User'}</td>
                          <td data-testid={`user-status-${u.id}`}>
                            {u.isActive ? 'Active' : 'Deactivated'}
                          </td>
                          <td>
                            {/*
                              Your own row carries no controls at all. The server refuses anyway;
                              rendering buttons whose only outcome is a refusal would be worse than
                              rendering none, and the reason is stated rather than left to a hover.
                            */}
                            {isYou ? (
                              <span className="muted" data-testid={`user-self-${u.id}`}>
                                Ask another administrator
                              </span>
                            ) : (
                              <>
                                <button
                                  data-testid={`user-active-${u.id}`}
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      async () => {
                                        await api.updateUser(u.id, { isActive: !u.isActive });
                                      },
                                      u.isActive
                                        ? `${u.name} has been deactivated and signed out.`
                                        : `${u.name} can sign in again.`,
                                    )
                                  }
                                >
                                  {u.isActive ? 'Deactivate' : 'Reactivate'}
                                </button>
                                <button
                                  data-testid={`user-admin-${u.id}`}
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      async () => {
                                        await api.updateUser(u.id, { isAdmin: !u.isAdmin });
                                      },
                                      u.isAdmin
                                        ? `${u.name} is no longer an administrator.`
                                        : `${u.name} is now an administrator.`,
                                    )
                                  }
                                >
                                  {u.isAdmin ? 'Remove admin' : 'Make admin'}
                                </button>
                                {/*
                                  RENAMING SOMEBODY ELSE. `adminUserPatchSchema` has accepted `name`
                                  since P8b and nothing ever sent it, so a misspelt account name was
                                  permanent — and it is the name everybody on the engagement reads
                                  against that person's decisions. A prompt rather than an inline
                                  field, matching the reset button beside it: this is a correction
                                  somebody makes once, not a form worth putting in every row.
                                */}
                                <button
                                  data-testid={`user-rename-${u.id}`}
                                  disabled={busy}
                                  onClick={() => {
                                    const next = window.prompt(`New name for ${u.name}`, u.name);
                                    if (!next || next.trim() === '' || next.trim() === u.name) return;
                                    void run(async () => {
                                      await api.updateUser(u.id, { name: next.trim() });
                                    }, `${u.name} is now ${next.trim()}.`);
                                  }}
                                >
                                  Rename
                                </button>
                                <button
                                  data-testid={`user-reset-${u.id}`}
                                  disabled={busy}
                                  onClick={() => {
                                    const next = window.prompt(
                                      `Set a new password for ${u.name}. They will be signed out, and you will need to give it to them directly.`,
                                    );
                                    if (!next) return;
                                    void run(async () => {
                                      await api.setUserPassword(u.id, next);
                                    }, `${u.name}'s password has been reset and their sessions ended.`);
                                  }}
                                >
                                  Reset password
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/*
                WHO REACHES WHAT, which nothing could answer. This modal listed accounts and
                `MembersEditor` listed one project's members; no route joined them, so "what does
                this consultant still reach" meant opening every project in turn — and only the ones
                the person asking was themselves a member of.

                GROUPED BY PERSON, because that is the direction the question is asked from: somebody
                rolls off, and you need everything they still hold. The rows come back flat so the
                other grouping stays available without a second route.
              */}
              <h5 className="asm-grid-h">Who reaches what</h5>
              <p className="muted asm-help">
                Every project grant on this deployment. Deactivating an account ends its sessions but
                does not withdraw its access — that is done per project, by an owner.
              </p>
              {access === null ? (
                <Loading what="the access list" testId="access-loading" />
              ) : access.length === 0 ? (
                <p className="muted" data-testid="access-empty">
                  Nobody has been granted access to a project yet.
                </p>
              ) : (
                <table className="tsa-table" data-testid="access-table">
                  <thead>
                    <tr>
                      <th scope="col">Person</th>
                      <th scope="col">Project</th>
                      <th scope="col">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {access.map((a) => (
                      <tr key={`${a.userId}:${a.projectId}`} data-testid={`access-${a.userId}-${a.projectId}`}>
                        <td>
                          {a.userName}
                          {!a.isActive && <span className="muted"> · deactivated</span>}
                        </td>
                        <td>
                          {a.projectName}
                          {/*
                            Access to an archived project is read-only, which is a materially
                            different fact from access to a live one — a list that flattened them
                            would overstate the reach.
                          */}
                          {a.archived && <span className="muted"> · archived</span>}
                        </td>
                        <td>{a.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/*
                WHAT HAS HAPPENED, as against who reaches what above. Neither answers the other's
                question: a grant list cannot tell you somebody was removed, and a log cannot tell
                you at a glance who is on what today.

                Until this existed the deployment kept no record at all of an account being created,
                deactivated, promoted or reset, or of a membership being granted, changed or
                withdrawn. `project_members.addedBy` was the only trace, the upsert overwrote it on
                every role change, removal destroyed it, and nothing read it.
              */}
              <h5 className="asm-grid-h">What has changed</h5>
              <p className="muted asm-help">
                Newest first. This log is append-only: there is no undo here and no annotation, which
                is the difference between it and the project change log.
              </p>
              {log === null ? (
                <Loading what="the access log" testId="access-log-loading" />
              ) : log.length === 0 ? (
                <p className="muted" data-testid="access-log-empty">
                  Nothing has been recorded yet.
                </p>
              ) : (
                <table className="tsa-table" data-testid="access-log-table">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">What</th>
                      <th scope="col">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((e) => (
                      <tr key={e.id} data-testid={`access-log-${e.id}`}>
                        <td className="muted">{new Date(e.at).toLocaleString()}</td>
                        <td>{describeAccessEvent(e)}</td>
                        {/*
                          "The deployment" rather than a blank: `bootstrapAdmin` creates the first
                          administrator with no actor, and an empty cell there reads as missing data
                          rather than as the fact it is.
                        */}
                        <td className="muted">{e.actorName ?? 'the deployment'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h5 className="asm-grid-h">Create an account</h5>
              <p className="muted asm-help">
                There is no sign-up page: an engagement workspace holding HR data should not accept
                self-registration. Accounts are made here and the password is handed over directly.
              </p>
              <div className="field">
                <label htmlFor="new-user-name">Name</label>
                <input
                  id="new-user-name"
                  data-testid="new-user-name"
                  value={name}
                  disabled={busy}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="new-user-email">Email</label>
                <input
                  id="new-user-email"
                  data-testid="new-user-email"
                  type="email"
                  value={email}
                  disabled={busy}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="new-user-password">Password (at least 12 characters)</label>
                <input
                  id="new-user-password"
                  data-testid="new-user-password"
                  type="password"
                  value={password}
                  disabled={busy}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <label className="check">
                <input
                  type="checkbox"
                  data-testid="new-user-admin"
                  checked={makeAdmin}
                  disabled={busy}
                  onChange={(e) => setMakeAdmin(e.target.checked)}
                />
                Can administer users
              </label>
              <button
                className="primary"
                data-testid="create-user"
                disabled={busy || !email.trim() || !name.trim() || !password}
                onClick={create}
              >
                Create the account
              </button>
            </>
          )}
    </Modal>
  );
}
